import { type Ref, computed, watch } from 'vue'

import type { HttpError } from '@backmarket/http-api'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import type { I18n } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import translations from '../ReviewCollection.translations'
import type { ReviewCollectionDisplayedError } from '../models/displayed-error'

function mapToDisplayedError({
  error,
  i18n,
  userEmail,
}: {
  error: HttpError | undefined
  i18n: I18n
  userEmail: string | undefined
}): ReviewCollectionDisplayedError | null {
  if (!error) return null

  const { statusCode } = error

  switch (statusCode) {
    case 401:
      return {
        title: i18n(translations.unauthorizedTitle),
        message: i18n(translations.unauthorizedMessage),
        type: 'not-connected',
      }

    case 403:
      return {
        title: i18n(translations.wrongAccountTitle),
        message: i18n(translations.wrongAccountMessage, {
          mail: userEmail,
        }),
        type: 'wrong-account',
      }

    default:
      return {
        title: i18n(translations.genericTitle),
        message: i18n(translations.genericMessage),
        type: 'other',
      }
  }
}

export function useDisplayedError(error: Ref<HttpError | undefined>) {
  const i18n = useI18n()
  const { user } = useUserStore()
  const logger = useLogger()

  watch(error, () => {
    if (error.value && ![403, 401].includes(error.value.statusCode)) {
      logger.error('[ReviewForm] GET', {
        error: error.value,
        owners: ['bot-squad-content-front'],
      })
    }
  })

  return computed(() =>
    mapToDisplayedError({
      error: error.value,
      i18n,
      userEmail: user.value.email,
    }),
  )
}
